<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		AdvancedCard,
		Alert,
		Container,
		Label,
		LabelV2,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';
	import HomeAvatar from 'src/components/Homes/HomeAvatar.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { homes } from 'src/stores/account/home';
	import { buildPendingHome } from 'src/helpers/houses/homes_helpers';
	import type { Home, HomeAvatar as HomeAvatarType } from 'src/types/Home';

	export let pendingHomeName = '';
	export let pendingHomeAvatar: HomeAvatarType | undefined;
	export let existingHomes: [string, string][] = [];
	export let deletionWarningHeading = '';

	const createFallbackHome = (id: string, name: string): Home => ({
		id,
		name,
		plan: 'standard',
		isActive: false,
		isShared: false,
	});

	const getDisplayHome = (unitId: string, unitName: string, homesList: Home[]): Home => {
		const home = homesList.find(({ id, isShared }) => id === unitId && !isShared);

		return home ?? createFallbackHome(unitId, unitName);
	};

	$: existingHomeRows = existingHomes.map(([unitId, unitName]) =>
		getDisplayHome(unitId, unitName, $homes.list),
	);

	$: pendingHome = pendingHomeName ? buildPendingHome(pendingHomeName, pendingHomeAvatar) : undefined;
</script>

<div>
	<Text element="h2" class="mb-m">
		{$format('settings_add_home_preview_heading', {
			default: 'Your homes will look like this',
		})}
	</Text>

	<Container type="modalContainer" padding="xs" class="pt-s pb-s pl-m pr-m">
		<Stack
			direction="row"
			directionMobile="row"
			align="between"
			alignMobile="none"
			gap="m"
			class="mb-s align-items-center"
		>
			<Text
				element={$showNewLayout ? undefined : 'h2'}
				styleType={$showNewLayout ? 'body' : undefined}
			>
				{$format('settings_unit_management_title', { default: 'Homes' })}
			</Text>
		</Stack>

		{#each existingHomeRows as home (home.id)}
			<div class="mb-s preview-home-card">
				<AdvancedCard title={home.name} class={$showNewLayout ? 'bg-color-0' : ''}>
					<HomeAvatar slot="left" {home} size="l" />
				</AdvancedCard>
			</div>
		{/each}

		{#if pendingHome}
			<div class="mb-s preview-home-card">
				<AdvancedCard title={pendingHomeName} selected class="border-primary">
					<HomeAvatar slot="left" home={pendingHome} size="l" />
					<div slot="right">
						{#if $showNewLayout}
							<LabelV2
								size="s"
								type="primary"
								value={$format('label_new_home', { default: 'New' })}
							/>
						{:else}
							<Label
								size="s"
								type="primary"
								value={$format('label_new_home', { default: 'New' })}
							/>
						{/if}
					</div>
				</AdvancedCard>
			</div>
		{/if}

		{#if deletionWarningHeading}
			<Alert type="warning" class="mt-s" heading={deletionWarningHeading} />
		{/if}
	</Container>
</div>

<style>
	.preview-home-card :global(.advanced-card) {
		overflow: visible;
	}

	.preview-home-card :global(.left-container) {
		padding-right: 10px;
		padding-bottom: 8px;
	}
</style>
